// Revise run — builder, Mark This Answer, and the skeleton's pick/evidence.

import type { BuilderScreen, EvidenceScreen, MtaScreen, PickScreen } from '../../../../../lib/revise/types';
import { gradeMta } from '../../../../../lib/revise/scoring';
import type { ReviseRunApi } from './useReviseRun';
import { Chip, html } from './ContentScreens';

export function Builder({ s, api }: { s: BuilderScreen; api: ReviseRunApi }) {
  const st = api.builder[s.id] ?? { filled: {}, tried: {} };
  const nextSlot = s.slots.findIndex((_, i) => st.filled[i] === undefined);
  const done = nextSlot === -1;
  return (
    <>
      {s.eyebrow && <span className="rr-eyebrow">{s.eyebrow}</span>}
      <h2>{s.heading}</h2>
      {s.lead && <p>{s.lead}</p>}
      {s.stand && (
        <div className="rr-note">
          <b>Your stand:</b> {s.stand}
        </div>
      )}
      <div className="rr-builder-para" aria-live="polite">
        {s.slots.map((slot, i) =>
          st.filled[i] !== undefined ? (
            <span key={i}> {slot.options[st.filled[i]].text}</span>
          ) : i === nextSlot ? (
            <span key={i} className="rr-placeholder"> [{slot.label}]</span>
          ) : null,
        )}
        {nextSlot === 0 && <span className="rr-placeholder">Your paragraph builds here.</span>}
      </div>
      {!done && (
        <>
          <div className="rr-slot-label">{s.slots[nextSlot].label}</div>
          <div className="rr-opts">
            {s.slots[nextSlot].options.map((o, oi) => {
              const tried = (st.tried[nextSlot] ?? []).includes(oi);
              return (
                <button key={oi} type="button" className={`rr-opt${tried ? ' rr-no' : ''}`} disabled={tried} onClick={() => api.chooseBuilder(s.id, nextSlot, oi, o.correct)}>
                  <span className="rr-letter" aria-hidden>
                    {oi + 1}
                  </span>
                  <span>
                    {o.text}
                    {tried && o.cost && <div className="rr-cost">{o.cost}</div>}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
      {done && s.close && <div className="rr-feedback rr-fb-ok">{s.close}</div>}
    </>
  );
}

export function Mta({ s, api }: { s: MtaScreen; api: ReviseRunApi }) {
  const r = api.mta[s.id] ?? { verdicts: {} };
  const checked = !!api.mtaChecked[s.id];
  const all = s.criteria.every((c) => r.verdicts[c.id]);
  const g = checked ? gradeMta(s, r) : null;
  return (
    <>
      <Chip text="Mark this answer" kind="accent" />
      <h2>{s.label}</h2>
      <div className="rr-script">{s.script}</div>
      <div className="rr-mta-crit">
        {s.criteria.map((c) => {
          const v = r.verdicts[c.id];
          const match = checked ? v === c.correct : null;
          return (
            <div key={c.id} className="rr-crit">
              <div>{c.prompt}</div>
              <div className="rr-seg" role="group" aria-label={c.prompt}>
                {(['yes', 'partial', 'no'] as const).map((opt) => (
                  <button key={opt} type="button" className={v === opt ? 'rr-sel' : ''} aria-pressed={v === opt} disabled={checked} onClick={() => api.setVerdict(s, c.id, opt)}>
                    {opt === 'yes' ? 'Yes' : opt === 'partial' ? 'Partly' : 'No'}
                  </button>
                ))}
              </div>
              {checked && (
                <div className={`rr-verdict ${match ? 'rr-ok' : 'rr-no'}`}>
                  <b>Examiner: {c.correct === 'yes' ? 'Yes' : c.correct === 'partial' ? 'Partly' : 'No'}.</b> {c.feedback}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!checked && (
        <div>
          <button type="button" className="rr-btn-primary" disabled={!all} onClick={() => api.checkMta(s)}>
            Check against the examiner
          </button>
        </div>
      )}
      {g && (
        <div className={`rr-feedback ${g.matches === g.of ? 'rr-fb-ok' : 'rr-fb-neutral'}`}>
          <b>
            You matched the examiner on {g.matches} of {g.of}.
          </b>
          {s.band && <span>This answer would score about {s.band}.</span>}
        </div>
      )}
    </>
  );
}

export function Pick({ s, api }: { s: PickScreen; api: ReviseRunApi }) {
  const chosen = api.picks[s.id];
  const done = chosen !== undefined;
  return (
    <>
      {s.tag && <Chip text={s.tag} />}
      <h2>{s.heading}</h2>
      {s.intro && <p>{s.intro}</p>}
      <div className="rr-opts" role="radiogroup">
        {s.options.map((o, i) => {
          let cls = 'rr-opt';
          if (done) cls += i === s.correct ? ' rr-ok' : i === chosen ? ' rr-no' : ' rr-dim';
          return (
            <button key={i} type="button" className={cls} disabled={done} aria-pressed={chosen === i} onClick={() => api.choosePick(s.id, i)}>
              <span className="rr-letter" aria-hidden>
                {i + 1}
              </span>
              <span>{o}</span>
            </button>
          );
        })}
      </div>
      {done && (
        <div className={`rr-feedback ${chosen === s.correct ? 'rr-fb-ok' : 'rr-fb-no'}`}>
          <b>{chosen === s.correct ? 'That one argues.' : 'Not that one.'}</b>
          <span {...html(s.feedback)} />
          {chosen !== s.correct && <p>Saved to your skeleton: the line that argues, not the one you chose.</p>}
        </div>
      )}
    </>
  );
}

export function Evidence({ s, api }: { s: EvidenceScreen; api: ReviseRunApi }) {
  const st = api.evidence[s.id] ?? { chosen: [], checked: false };
  return (
    <>
      {s.tag && <Chip text={s.tag} />}
      <h2>{s.heading}</h2>
      <div className="rr-note">
        <b>The claim:</b> {s.claim}
      </div>
      {s.intro && <p>{s.intro}</p>}
      <div className="rr-plan">
        {s.items.map((it, i) => {
          const on = st.chosen.includes(i);
          const cls = st.checked ? (on === it.use ? ' rr-ok' : ' rr-no') : '';
          return (
            <div key={i} className={`rr-ev-item${cls}`}>
              <span>{it.text}</span>
              <button type="button" className={on ? 'rr-sel' : ''} aria-pressed={on} disabled={st.checked} onClick={() => api.toggleEvidence(s.id, i)}>
                {on ? 'Using it' : 'Use it'}
              </button>
              {st.checked && !it.use && it.off && <small>Leave it out: {it.off}</small>}
              {st.checked && it.use && !on && <small>This one backs the claim. It is in your skeleton.</small>}
            </div>
          );
        })}
      </div>
      {!st.checked && (
        <div>
          <button type="button" className="rr-btn-primary" disabled={st.chosen.length === 0} onClick={() => api.checkEvidence(s.id)}>
            Check my evidence
          </button>
        </div>
      )}
      {st.checked && (
        <div className="rr-feedback rr-fb-neutral">
          <span {...html(s.feedback)} />
        </div>
      )}
    </>
  );
}
